import { SensorLatest } from '../../services/api';
import { HoursSelection } from '../../state/types';
import { SensorHistoryPlotItem, SensorMetricPlotItem } from '../Plots/plotCommon';

export enum MetricOptions {
  Raw = 'Raw',
  Hourly = 'Hourly',
  Daily = 'Daily',
}
export const getCommonLocId = (
  selectedSensorIds: string[],
  sensorsById: Map<string, SensorLatest>
) => {
  // Get all locations and filter out empty strings and duplicates
  const allLocations: string[] = Array.from(
    new Set(
      selectedSensorIds
        .map((id: string) => sensorsById.get(id)?.location ?? '')
        .filter((loc: string) => loc !== '')
    )
  );

  // If no locations or only one location, return it
  if (allLocations.length === 0) return '';
  if (allLocations.length === 1) return allLocations[0];

  // Find common prefix among all locations
  let commonPrefix: string = allLocations[0];

  for (let i = 1; i < allLocations.length; i++) {
    let j = 0;
    while (
      j < commonPrefix.length &&
      j < allLocations[i].length &&
      commonPrefix[j] === allLocations[i][j]
    ) {
      j++;
    }
    commonPrefix = commonPrefix.substring(0, j);

    // Stop early if no common prefix
    if (commonPrefix === '') break;
  }

  // Trim to last complete segment (include everything up to and including the last '#')
  const lastHash: number = commonPrefix.lastIndexOf('#');
  if (lastHash >= 0) {
    commonPrefix = commonPrefix.substring(0, lastHash + 1);
  }

  // If result is just '#', return it as is
  if (commonPrefix === '#' || commonPrefix === '') {
    return commonPrefix || '#';
  }

  // Remove trailing '#' if there's more content before it
  if (commonPrefix.endsWith('#') && commonPrefix.length > 1) {
    commonPrefix = commonPrefix.substring(0, commonPrefix.length - 1);
  }

  return commonPrefix || allLocations[0];
};

export const filterHistoryCSVdata = (
  selectedHours: HoursSelection,
  sensorData: SensorHistoryPlotItem[],
  sensorsById: Map<string, SensorLatest>
) => {
  const { startHour, endHour, includeWeekends } = selectedHours;

  return sensorData.map((sensor) => {
    const timeZone = sensorsById.get(sensor.sensorId)?.timeZone ?? 'Europe/London';
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      weekday: 'short',
      hour12: false,
    });
    const { time: timeArray, value: valueArray } = sensor.history;

    const filteredTimes: number[] = [];
    const filteredValues: number[] = [];

    timeArray.forEach((timestamp, i) => {
      const parts = formatter.formatToParts(new Date(timestamp * 1000));
      const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10) % 24;
      const weekday = parts.find((p) => p.type === 'weekday')?.value ?? 'Mon';
      const isWeekend = weekday === 'Sun' || weekday === 'Sat';

      // Skip if it's a weekend and weekends are not included
      if (!includeWeekends && isWeekend) {
        return;
      }

      // Check if time is within working hours
      if (hour >= startHour && hour < endHour) {
        filteredTimes.push(timestamp);
        filteredValues.push(valueArray[i]);
      }
    });

    return {
      ...sensor,
      history: {
        time: filteredTimes,
        value: filteredValues,
      },
    };
  });
};

// filter weekends data, for Hourly data filter by hour, for daily data filter by includeWeekends boolean
export const filterMetricsCSVData = (
  selectedHours: HoursSelection,
  sensorData: SensorMetricPlotItem[],
  sensorsById: Map<string, SensorLatest>
): SensorMetricPlotItem[] => {
  const { startHour, endHour, includeWeekends } = selectedHours;

  return sensorData.map((sensor) => {
    const timeZone = sensorsById.get(sensor.sensorId)?.timeZone ?? 'Europe/London';

    const weekdayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
    });

    const filteredMetrics = sensor.metrics.filter((m) => {
      if (!m.date) return true;

      const hasHourlyTime = m.date.includes(' '); // distinguishes "2026-02-11" from "2026-02-11 00:00:00"

      if (hasHourlyTime) {
        // "2026-02-11 00:00:00" — parse as UTC by replacing space with T and appending Z
        const date = new Date(`${m.date.replace(' ', 'T')}Z`);

        // Weekend filter
        if (!includeWeekends) {
          const weekday =
            weekdayFormatter.formatToParts(date).find((p) => p.type === 'weekday')?.value ?? 'Mon';
          if (weekday === 'Sat' || weekday === 'Sun') return false;
        }

        // Hour filter
        const hour = date.getUTCHours();
        if (hour < startHour || hour >= endHour) return false;

        return true;
      }
      // "2026-02-11" — date only, no hour filtering
      if (includeWeekends) return true;

      const date = new Date(`${m.date}T00:00:00Z`);
      const weekday =
        weekdayFormatter.formatToParts(date).find((p) => p.type === 'weekday')?.value ?? 'Mon';
      return weekday !== 'Sat' && weekday !== 'Sun';
    });

    return {
      ...sensor,
      metrics: filteredMetrics,
    };
  });
};
